import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { sportsMasterDataApi, teamGroupsMasterDataApi } from "@/apis/apis";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";

import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import ImageInput from "@/components/ImageInput/Index";
import { LIST_MODE } from "@/constant/Lists/listMode";
import LocalizedInput from "@/components/LocalizedInput";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import { TeamGroupInitialValues } from "@/data/WebDataManagement/MasterData/TeamGroups/initial-values";
import TeamGroupSchema from "@/schema/WebDataManagement/MasterData/TeamGroups/TeamGroupSchema";
import axiosInstance from "@/Axios/ConfiguredAxios";
import { debounce } from "@mui/material";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";
import { booleanOptionFormOptions } from "@/constant/GeneralOptions/boolean";
import RadioInput from "@/components/RadioInput/Index";

const TeamGroupModal = forwardRef((_, ref) => {
  const [sportId, setSportId] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [defaultSports, setDefaultSports] = useState([]);
  const [defaultParentTeamGroups, setDefaultParentTeamGroups] = useState([]);
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const { accessToken } = useSelector((state) => state?.authReducer);
  const booleanOptionFormOptionsMap = booleanOptionFormOptions();

  const [loading, setLoading] = useState({
    parentTeamGroupsList: false,
    sportsList: false,
  });

  const showApi = modalState.id
    ? `${teamGroupsMasterDataApi}/${modalState.id}`
    : null;

  const {
    data: showData,
    isSuccess: showIsSuccess,
    error: showError,
  } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  useEffect(() => {
    if (showIsSuccess) {
      setSportId(showData.data.sport.id);
      setParentId(showData.data.parent.id);
    }
  }, [showIsSuccess, showData]);

  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );
  const localeCodes = useMemo(
    () => locales?.map((locale) => locale.code) ?? [],
    [locales]
  );

  const {
    mutate: save,
    error: saveError,
    isPending: saveIsPending,
  } = usePost({
    apiURL: modalState.id
      ? `${teamGroupsMasterDataApi}/${modalState.id}?_method=PUT`
      : teamGroupsMasterDataApi,
    isFormData: true,
    onSuccess: (data) => {
      closeModal();
      RenderToast({
        type: "success",
        message: `Team Group ${
          modalState.id ? "edited" : "added"
        } successfully.`,
      });
    },
  });

  const sportslistApi = `${sportsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;
  const teamGroupslistApi = `${teamGroupsMasterDataApi}?mode=${LIST_MODE.ALL}&filter[keyword]=`;

  useEffect(() => {
    if (!modalState.isOpen) return;

    const fetchDefaultSports = async () => {
      setLoading((prev) => ({ ...prev, sportsList: true }));
      const response = await axiosInstance.get(sportslistApi);

      if (response) {
        const options = response.data.data.map((data) => ({
          label: `${data.code} - ${data.name}`,
          value: data.id,
        }));

        const sportId = showData?.data?.sport?.id;
        if (sportId && !options.some((opt) => opt.value === sportId)) {
          // Add the sport to the top of the options list
          options.unshift({
            label: `${showData.data.sport.code} - ${showData.data.sport.name}`,
            value: showData.data.sport.id,
          });
        }

        options.unshift({ label: "No sport selected", value: null });

        setDefaultSports(options);
      }

      setLoading((prev) => ({ ...prev, sportsList: false }));
    };

    const fetchDefaultParentTeamGroups = async () => {
      setLoading((prev) => ({ ...prev, parentTeamGroupsList: true }));
      const response = await axiosInstance.get(teamGroupslistApi);

      if (response) {
        const options =
          response.data.data
            .filter((val) => val.level === 1)
            .filter((val) => val.id !== modalState.id) // Do not display the selected id
            .map((val) => ({
              label: `${val.code} - ${val.name}`,
              value: val.id,
            })) ?? [];

        const parentId = showData?.data?.parent?.id;
        if (parentId && !options.some((opt) => opt.value === parentId)) {
          // Add the parent to the top of the options list
          options.unshift({
            label: `${showData.data.parent.code} - ${showData.data.parent.name}`,
            value: showData.data.parent.id,
          });
        }

        options.unshift({ label: "No parent selected", value: null });

        setDefaultParentTeamGroups(options);
      }

      setLoading((prev) => ({ ...prev, parentTeamGroupsList: false }));
    };

    fetchDefaultSports();
    fetchDefaultParentTeamGroups();
  }, [accessToken, teamGroupslistApi, showData, modalState, sportslistApi]);

  const debouncedFetchSports = debounce(async (input, callback) => {
    setLoading((prev) => ({ ...prev, sportsList: true }));
    const response = await axiosInstance.get(`${sportslistApi}${input}`);

    if (response) {
      callback(
        response.data.data.map((item) => ({
          label: `${item.code} - ${item.name}`,
          value: item.id,
        })) ?? []
      );
    }

    setLoading((prev) => ({ ...prev, sportsList: false }));
  }, 500);
  const debouncedFetchParentTeamGroups = debounce(async (input, callback) => {
    setLoading((prev) => ({ ...prev, parentTeamGroupsList: true }));
    const response = await axiosInstance.get(`${teamGroupslistApi}${input}`);

    if (response) {
      callback(
        response.data.data
          .filter((data) => data.level === 1)
          .map((item) => ({
            label: `${item.code} - ${item.name}`,
            value: item.id,
          })) ?? []
      );
    }

    setLoading((prev) => ({ ...prev, parentTeamGroupsList: false }));
  }, 500);

  const getSportSelectedOption = () => {
    // doesnt select anything on purpose
    if (sportId === null && sportId !== formik.initialValues.sport_id)
      return null;

    return sportId
      ? defaultSports.find((opt) => opt.value === sportId)
      : showData?.data?.sport?.code // select the showed data value
      ? {
          label: `${showData?.data?.sport?.code} - ${showData?.data?.sport?.name}`,
          value: sportId,
        }
      : null;
  };
  const getParentTeamGroupSelectedOption = () => {
    // doesnt select anything on purpose
    if (parentId === null && parentId !== formik.initialValues.parent_id)
      return null;

    return parentId
      ? defaultParentTeamGroups.find((opt) => opt.value === parentId) // select the value
      : showData?.data?.parent?.code // select the showed data value
      ? {
          label: `${showData?.data?.parent?.code} - ${showData?.data?.parent?.name}`,
          value: parentId,
        }
      : null;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: TeamGroupInitialValues(
      localeCodes,
      modalState.id ? showData : null
    ),
    validationSchema: TeamGroupSchema(localeCodes, !!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    formData.append("sport_id", values.sport_id);
    if (values.parent_id) {
      formData.append("parent_id", values.parent_id);
    }
    formData.append("code", values.code);
    formData.append("status", values.status);
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        formData.append(`localizations[${key}]`, values.localizations[key]);
      }
    });
    if (values.icon) {
      formData.append("icon", values.icon);
    }
    formData.append("is_icon_bordered", values.is_icon_bordered);

    save(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    setSportId(null);
    setParentId(null);
    setModalState({ isOpen: false, id: null });
  }, [formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Team Group" : "Add Team Group"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Sport ID"
            placeholder="No sport selected. Type to search..."
            defaultOptions={defaultSports}
            loadOptions={debouncedFetchSports}
            isLoading={loading.sportsList}
            value={getSportSelectedOption()}
            onChange={(selected) => {
              setSportId(selected?.value || null);
              formik.setFieldValue("sport_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            required={false}
            label="Parent ID"
            placeholder="No parent selected. Type to search..."
            defaultOptions={defaultParentTeamGroups}
            loadOptions={debouncedFetchParentTeamGroups}
            isLoading={loading.parentTeamGroupsList}
            value={getParentTeamGroupSelectedOption()}
            onChange={(selected) => {
              setParentId(selected?.value || null);
              formik.setFieldValue("parent_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
          />
        </Col>
      </Row>
      <LocalizedInput
        formik={formik}
        error={saveError}
        isEdit={!!modalState.id}
      />
      <Row>
        <Col md={12}>
          <ImageInput
            id="icon"
            formFormik={formik}
            fieldName="icon"
            label="Icon"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.icon_url}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <RadioInput
            options={booleanOptionFormOptionsMap}
            formik={formik}
            saveError={saveError}
            fieldName="is_icon_bordered"
            label="Icon Border?"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default TeamGroupModal;
